import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Key, RefreshCw, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * PHI Decryption Tool Component
 * 
 * A standalone tool for super admins to decrypt PHI data from the database.
 * All usage is logged for HIPAA compliance.
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onOpenChange - Callback when dialog open state changes
 */
export const DecryptionTool = ({ open, onOpenChange }) => {
  const [decryptInput, setDecryptInput] = useState('');
  const [decryptResult, setDecryptResult] = useState(null);
  const [decryptLoading, setDecryptLoading] = useState(false);
  const [decryptError, setDecryptError] = useState(null);

  const handleDecrypt = async () => {
    if (!decryptInput.trim()) {
      toast.error('Please paste encrypted data');
      return;
    }
    
    setDecryptLoading(true);
    setDecryptError(null);
    setDecryptResult(null);
    
    try {
      // Try to parse as JSON first
      let dataToDecrypt;
      try {
        dataToDecrypt = JSON.parse(decryptInput);
      } catch {
        // Not JSON, treat as single string
        dataToDecrypt = decryptInput.trim();
      }
      
      const response = await axios.post(`${API}/admin/tools/decrypt`, {
        data: dataToDecrypt
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      
      setDecryptResult(response.data);
      toast.success(`Decrypted ${response.data.fields_decrypted} field(s) - This action has been logged`);
    } catch (error) {
      console.error('Decryption failed:', error);
      setDecryptError(error.response?.data?.detail || 'Decryption failed');
      toast.error('Decryption failed');
    } finally {
      setDecryptLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setDecryptInput('');
    setDecryptResult(null);
    setDecryptError(null);
  };

  const handleCopyResult = () => {
    navigator.clipboard.writeText(
      typeof decryptResult.decrypted === 'string' 
        ? decryptResult.decrypted 
        : JSON.stringify(decryptResult.decrypted, null, 2)
    );
    toast.success('Copied to clipboard');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            <Key className="h-5 w-5" />
            PHI Decryption Tool
          </DialogTitle>
          <DialogDescription>
            Paste encrypted data from the database to decrypt. All usage is logged for HIPAA compliance.
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-amber-800 font-medium">Warning: Security Notice</p>
          <p className="text-xs text-amber-700 mt-1">
            This tool is for data recovery and troubleshooting only. Every use is recorded in the audit log.
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label>Encrypted Data</Label>
            <p className="text-xs text-gray-500 mb-2">
              Paste a single encrypted string (gAAAAA...) or a JSON object with encrypted fields
            </p>
            <textarea
              className="w-full h-40 p-3 border rounded-lg font-mono text-sm bg-gray-50"
              placeholder='Single value: gAAAAABh7x2K3...\n\nOr JSON:\n{\n  "first_name": "gAAAAABh...",\n  "last_name": "gAAAAABh..."\n}'
              value={decryptInput}
              onChange={(e) => setDecryptInput(e.target.value)}
            />
          </div>
          
          <Button 
            onClick={handleDecrypt} 
            disabled={decryptLoading || !decryptInput.trim()}
            className="w-full bg-amber-600 hover:bg-amber-700"
          >
            {decryptLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Decrypting...
              </>
            ) : (
              <>
                <Key className="h-4 w-4 mr-2" />
                Decrypt Data
              </>
            )}
          </Button>
          
          {decryptError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">Error</p>
              <p className="text-sm text-red-700">{decryptError}</p>
            </div>
          )}
          
          {decryptResult && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className={decryptResult.fields_decrypted > 0 ? "text-green-800" : "text-amber-800"}>
                  {decryptResult.fields_decrypted > 0 ? "Decrypted Result" : "Result"}
                </Label>
                <Badge className={decryptResult.fields_decrypted > 0 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
                  {decryptResult.fields_decrypted} field(s) decrypted
                </Badge>
              </div>
              
              {decryptResult.note && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">{decryptResult.note}</p>
                </div>
              )}
              
              <div className={`${decryptResult.fields_decrypted > 0 ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"} border rounded-lg p-3`}>
                <pre className="whitespace-pre-wrap text-sm font-mono text-gray-900 overflow-x-auto">
                  {typeof decryptResult.decrypted === 'string' 
                    ? decryptResult.decrypted 
                    : JSON.stringify(decryptResult.decrypted, null, 2)}
                </pre>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyResult}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy Result
              </Button>
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DecryptionTool;
